import type { APIListResponse } from '../types/api';

export enum Action {
    READ = 'READ',
    RUN = 'RUN',
    WRITE = 'WRITE',
    DEPLOY = 'DEPLOY',
    DENY = 'DENY'
}
export interface Permission {
    action: Action;
    vrn: string;
}

/**
 * Free-form role metadata. `externalId` maps the role to an IdP group (e.g. a Microsoft Entra
 * group Object ID) so SCIM provisioning can match an existing role by externalId.
 */
export interface RoleTags {
    externalId?: string;
    [key: string]: unknown;
}
/**
 * @interface SaveRole Represents the data structure for saving a new role.
 * @property {string} roleName - The name of the role.
 * @property {string} description - A brief description of the role.
 * @property {string} permissions - A comma-separated list of permissions associated with the role.
 * @property {string} tenantId - The tenant ID to which the role belongs.
 * @property {boolean} active - Indicates if the role is active.
 * @property {RoleTags} tags - Optional free-form metadata (e.g. externalId for SCIM/IdP mapping).
 */
export interface SaveRole {
    roleName: string,
    description: string,
    permissions: Permission[],
    tenantId: string,
    active: boolean,
    tags?: RoleTags
}

/**
 * @interface UpdateRoles Represents the data structure for updating an existing role.
 * @property {string} roleName - The name of the role.
 * @property {string} description - A brief description of the role.
 * @property {string} permissions - A comma-separated list of permissions associated with the role.
 * @property {string} tenantId - The tenant ID to which the role belongs.
 * @property {boolean} active - Indicates if the role is active.
 * @property {string | string[]} roleId - The unique identifier of the role.
 * @property {RoleTags} tags - Optional free-form metadata (e.g. externalId for SCIM/IdP mapping).
 */
export interface UpdateRoles {
    roleName: string,
    description: string,
    permissions: Permission[],
    tenantId: string,
    active: boolean,
    roleId: string | string[],
    tags?: RoleTags
}

/**
 * @interface Role Represents the Attributes of a single role in the system.
 * @property {boolean} active - Indicates if the role is active.
 * @property {string} modified - The timestamp when the role was last modified.
 * @property {string} roleName - The name of the role.
 * @property {string} permissions - A comma-separated list of permissions associated with the role.
 * @property {string} roleId - The unique identifier of the role.
 * @property {string} entity - The entity type of the record.
 * @property {string} description - A brief description of the role.
 * @property {string} tenantId - The tenant ID to which the role belongs.
 * @property {string} created - The timestamp when the role was created.
 * @property {string} modifiedBy - The identifier of the user who last modified the role.
 * @property {string} createdBy - The identifier of the user who created the role.
 * @property {RoleTags} tags - Optional free-form metadata (e.g. externalId for SCIM/IdP mapping).
 */
export interface Role {
    active: boolean;
    modified: string;
    roleName: string;
    permissions: Permission[];
    roleId: string;
    entity: string;
    description?: string;
    tenantId: string;
    created: string;
    modifiedBy?: string;
    createdBy?: string;
    tags?: RoleTags;
}

/**
 * @interface RolesListResponse Represents the response structure for a list of roles.
 * @property {number} count - The total number of roles.
 * @property {Role[]} items - The list of roles.
 */
export interface RolesListResponse {
    count: number;
    items: Role[];
    lastKey: string;
}

export interface ResourceOption {
    value: string;
    text: string;
}
export interface VRNConfigItem {
    app: string;
    resource: string;
    resourceName: string;
    vrn: string;
    action: string;
    resourceNamesList: ResourceOption[];
    loading: boolean;
}

export type RoleResponse = APIListResponse<Role>;
